import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Img from "@insite/mobius/Img";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import PromoCodeModal from "@SupplyLand/Components/PromoCodeModal";
import subscribe from "@SupplyLand/Handlers/CouponSubscribe";
import { CustomFlyoutDrawerState } from "@SupplyLand/Store/Components/FlyoutDrawerReducer";
import parse from "html-react-parser";
import React, { FocusEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    subheading = "subheading",
    image = "image",
    mobileImage = "mobileImage",
    showOnce = "showOnce",
    promoMessage = "promoMessage",
    promoCodeValue = "promoCodeValue",
    promoButton = "promoButton",
}

interface OwnProps extends WidgetProps {
    id: string;
    subscribe: (params: { email: string; onSuccess: () => void }) => void;
    fields: {
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.image]: string;
        [fields.mobileImage]: string;
        [fields.showOnce]: boolean;
        [fields.promoMessage]: string;
        [fields.promoCodeValue]: string;
        [fields.promoButton]: string;
    };
}

const mapDispatchToProps = {
    subscribe,
};

const mapStateToProps = (state: CustomFlyoutDrawerState) => {
    return {
        isDrawerOpen: state.xcentiumFlyoutDrawer.flyoutDrawerIsOpen,
    };
};

type Position = "left" | "top" | "right" | "bottom";

export interface baseDrawerStyles {
    drawer?: DrawerPresentationProps;
    drawerInShell?: DrawerPresentationProps;
    wrapper: InjectableCss;
    position?: Position;
}

const baseDrawerStyles: DrawerPresentationProps = {
    size: 200,
    closeButtonProps: {
        shape: "rectangle",
        buttonType: "solid",
        color: "white",
        size: 50,
    },
    closeButtonIconProps: {
        src: "X",
        size: 20,
    },
    cssOverrides: {
        drawerTitle: css`
            background: white;
        `,
        drawerContent: css`
            max-height: 550px;
            overflow-y: inherit;
            radius: 6px;
        `,
        drawerBody: css`
            background: ${getColor("common.background")};
            height: auto;
            position: absolute;
            overflow: inherit;
        `,
        drawerContainer: css`
            position: relative;
            left: 50%;
            top: 25%;
            transform: translateX(-50%);
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 700px;
                    `,
                    css`
                        width: 700px;
                    `,
                    css`
                        width: 700px;
                    `,
                ]);
            }}
        `,
    },
    position: "top",
};

export interface CouponModalStyles {
    wrapper: InjectableCss;
    miscGridItem?: GridItemProps;
    headingText: TypographyPresentationProps;
    subheadingText: TypographyPresentationProps;
    formWrapper?: InjectableCss;
    emailGridItem: GridItemProps;
    emailInput: TextFieldPresentationProps;
    btnGridItem: GridItemProps;
    unlockButton: ButtonPresentationProps;
    declineText: TypographyPresentationProps;
    imageGridItem?: GridItemProps;
    imageStyles?: InjectableCss;
    imageWrapper?: InjectableCss;
    container?: GridContainerProps;
    innerContainer?: GridContainerProps;
    contentGridItem?: GridItemProps;
    mobileImage?: InjectableCss;
    mobileImageGridItem?: GridItemProps;
    mobileImageWrapper?: InjectableCss;
    agreementText?: TypographyPresentationProps;
}

export const couponModalStyles: CouponModalStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background-color: ${getColor("common.background")};
            border-radius: 8px;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding-bottom: 10px;
                    `,
                    css`
                        padding-right: 10px;
                        padding-bottom: 30px;
                    `,
                    css`
                        padding-right: 10px;
                        padding-bottom: 30px;
                    `,
                    css`
                        padding-right: 10px;
                        padding-bottom: 30px;
                    `,
                    css`
                        padding-right: 10px;
                        padding-bottom: 30px;
                    `,
                ]);
            }}
        `,
    },
    miscGridItem: {
        width: 12,
    },
    container: {
        gap: 10,
        css: css``,
    },
    innerContainer: {
        gap: 20,
        css: css`
            padding-left: 20px;
        `,
    },
    headingText: {
        css: css`
            font-size: 32px;
            text-align: center;
            width: 100%;
            font-weight: bold;
            color: #000; /* Adjust the text color as needed */
        `,
    },
    subheadingText: {
        css: css`
            font-size: 16px;
            text-align: center;
            width: 100%;
            color: #000; /* Adjust the text color as needed */
        `,
    },
    formWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            width: 100%;
        `,
    },
    emailGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
            width: 350px;
        `,
    },
    emailInput: {
        css: css`
            text-align: center;
        `,
    },
    btnGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
            width: 350px;
        `,
    },
    unlockButton: {
        css: css`
            width: 100%;
            color: black;
        `,
    },
    declineText: {
        css: css`
            font-size: 12px;

            width: 100%;
            text-align: center;
            cursor: pointer;
            &:hover {
                text-decoration: underline;
            }
        `,
    },
    imageGridItem: {
        width: [0, 3, 3, 3, 3],
    },
    imageStyles: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    imageWrapper: {
        css: css`
            height: 100%;
            position: absolute;
            top: 0;
            left: 0;
        `,
    },
    mobileImageWrapper: {
        css: css`
            width: 100%;
        `,
    },
    mobileImage: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    mobileImageGridItem: {
        width: [12, 0, 0, 0, 0],
    },
    contentGridItem: {
        width: [12, 9, 9, 9, 9],
    },
    agreementText: {
        css: css`
            font-size: 10px;
            color: #2d3435;
            width: 100%;
            text-align: center;
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");

export const CouponModal = ({ id, fields, subscribe }: Props) => {
    const emailRequiredFieldMessage = siteMessage("EmailSubscription_EmailIsRequiredErrorMessage");
    const emailFieldMessage = siteMessage("EmailSubscription_EmailIsInvalidErrorMessage");

    const [isOpen, setDrawerIsOpen] = useState<boolean>(false);
    const [emailPlaceholder, setEmailPlaceholder] = useState("Enter your email");
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [promoCodeModalOpen, setPromoCodeModalOpen] = useState<boolean>(false);

    const agreementText = "By signing up, you agree to receive email marketing.";

    useEffect(() => {
        // Retrieve the 'modalShown' status from localStorage
        const modalShown = localStorage.getItem("modalShown") === "true";

        if (!modalShown || !fields.showOnce) {
            setDrawerIsOpen(true);
            if (!modalShown) {
                localStorage.setItem("modalShown", "true");
            }
        } else {
            setDrawerIsOpen(false);
        }
    }, [fields.showOnce]);

    const onSubscribeClick = () => {
        setIsSubmitted(true);
        if (!validateEmail(email)) {
            return;
        }

        subscribe({
            email,
            onSuccess: () => {
                setDrawerIsOpen(false);
                setPromoCodeModalOpen(true);
            },
        });
    };

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const handleEmailFocus = () => {
        setEmailPlaceholder("");
    };

    const handleEmailBlur = (e: FocusEvent<HTMLInputElement>) => {
        if (!e.target.value) {
            setEmailPlaceholder("Enter your email");
        }
    };

    const handleCloseModal = () => {
        setDrawerIsOpen(false);
    };

    const { promoMessage, promoCodeValue, promoButton } = fields;

    return (
        <>
            <Drawer
                {...baseDrawerStyles}
                isOpen={isOpen}
                handleClose={handleCloseModal}
                data-test-selector="couponModal"
            >
                <StyledWrapper {...couponModalStyles.wrapper}>
                    <GridContainer {...couponModalStyles.container}>
                        {fields.image && (
                            <GridItem {...couponModalStyles.imageGridItem}>
                                <StyledWrapper {...couponModalStyles.imageWrapper}>
                                    <Img src={fields.image} loading="lazy" {...couponModalStyles.imageStyles} />
                                </StyledWrapper>
                            </GridItem>
                        )}
                        <GridItem {...couponModalStyles.contentGridItem}>
                            <GridContainer {...couponModalStyles.innerContainer}>
                                {fields.mobileImage && (
                                    <GridItem {...couponModalStyles.mobileImageGridItem}>
                                        <StyledWrapper {...couponModalStyles.mobileImageWrapper}>
                                            <Img
                                                src={fields.mobileImage}
                                                loading="lazy"
                                                {...couponModalStyles.imageStyles}
                                            />
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                                <GridItem {...couponModalStyles.miscGridItem}>
                                    {fields.heading && (
                                        <Typography {...couponModalStyles.headingText}>
                                            {parse(fields.heading, parserOptions)}
                                        </Typography>
                                    )}
                                </GridItem>
                                <GridItem {...couponModalStyles.miscGridItem}>
                                    {fields.subheading && (
                                        <Typography {...couponModalStyles.subheadingText}>
                                            {parse(fields.subheading, parserOptions)}
                                        </Typography>
                                    )}
                                </GridItem>
                                <StyledWrapper data-test-selector="form-wrapper" {...couponModalStyles.formWrapper}>
                                    <GridItem {...couponModalStyles.emailGridItem}>
                                        <TextField
                                            {...couponModalStyles.emailInput}
                                            placeholder={emailPlaceholder}
                                            onFocus={handleEmailFocus}
                                            onBlur={handleEmailBlur}
                                            id={id}
                                            value={email}
                                            onChange={e => emailChangeHandler(e.currentTarget.value)}
                                            error={isSubmitted && emailError}
                                        />
                                    </GridItem>
                                    <GridItem {...couponModalStyles.btnGridItem}>
                                        <Button
                                            {...couponModalStyles.unlockButton}
                                            onClick={onSubscribeClick}
                                            disabled={isSubmitted && !!emailError}
                                        >
                                            UNLOCK OFFER
                                        </Button>
                                    </GridItem>
                                </StyledWrapper>
                                <GridItem {...couponModalStyles.miscGridItem}>
                                    <Typography {...couponModalStyles.agreementText}>{agreementText}</Typography>
                                </GridItem>
                                <GridItem {...couponModalStyles.miscGridItem}>
                                    <Typography {...couponModalStyles.declineText} onClick={handleCloseModal}>
                                        No, thanks
                                    </Typography>
                                </GridItem>
                                <Zone zoneName="Container" contentId={id} />
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </Drawer>
            <PromoCodeModal
                id={id}
                isOpen={promoCodeModalOpen}
                onClose={() => setPromoCodeModalOpen(false)}
                promoMessage={promoMessage}
                promoCodeValue={promoCodeValue}
                promoButton={promoButton}
            />
        </>
    );
};

const formTab = {
    displayName: "Form Modal",
    sortOrder: 0,
};

const promoTab = {
    displayName: "Promo Modal",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CouponModal),
    definition: {
        displayName: "Email Offer Modal",
        group: "Basic",
        icon: "Mail",
        fieldDefinitions: [
            {
                fieldType: "Contextual",
                name: fields.heading,
                displayName: "Heading",
                editorTemplate: "RichTextField",
                defaultValue: "",
                extendedConfig: { height: 100 },
                tab: formTab,
            },
            {
                fieldType: "Contextual",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                tab: formTab,
            },
            {
                fieldType: "General",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: formTab,
            },
            {
                fieldType: "General",
                name: fields.mobileImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: formTab,
            },
            {
                fieldType: "General",
                name: fields.showOnce,
                editorTemplate: "CheckboxField",
                displayName: "Show on First Load Only",
                defaultValue: false,
                tab: formTab,
            },
            {
                fieldType: "General",
                name: fields.promoMessage,
                editorTemplate: "RichTextField",
                displayName: "Promo Modal Message",
                defaultValue: "",
                extendedConfig: { height: 100 },
                tab: promoTab,
            },
            {
                fieldType: "General",
                name: fields.promoCodeValue,
                editorTemplate: "TextField",
                displayName: "Promo Code Value",
                defaultValue: "",
                tab: promoTab,
            },
            {
                fieldType: "General",
                name: fields.promoButton,
                editorTemplate: "TextField",
                displayName: "Button Text",
                defaultValue: "Okay!",
                tab: promoTab,
            },
        ],
    },
};

export default widgetModule;
